import { fetchSpaceActivityLog as fetchSpaceActivityLogApi } from '@/spaces/api/spaces.api';
import type { ActivityLogAction, ActivityLogEntry, ActivityLogParentLog } from '@/spaces/types/types';
import { type Ref, ref } from 'vue';

const activityLogEntries: Ref<ActivityLogEntry[]> = ref([]);
const fetchingActivityLog = ref(false);
const hasMore = ref(false);
const nextCursor = ref<string | undefined>(undefined);
const currentSpaceId = ref<string | undefined>(undefined);

let localIdCounter = 0;

interface AddLocalLogPayload {
    action: ActivityLogAction;
    itemType: string;
    itemId: string;
    itemDisplayName: string;
    userId: string;
    parentLog?: ActivityLogParentLog;
}

/**
 * Activity log state + fetch orchestration for a single space.
 *
 * Uses a plain ref (not `useList`) because the API returns entries in newest-first order
 * and that ordering must be preserved in the UI.
 */
export function useSpacesActivityLog () {
    async function fetchActivityLog (spaceId: string) {
        fetchingActivityLog.value = true;
        currentSpaceId.value = spaceId;

        try {
            const response = await fetchSpaceActivityLogApi(spaceId);
            activityLogEntries.value = response.entries;
            hasMore.value = response.hasMore;
            nextCursor.value = response.nextCursor;
        } catch (error) {
            activityLogEntries.value = [];
            hasMore.value = false;
            nextCursor.value = undefined;
            throw error;
        } finally {
            fetchingActivityLog.value = false;
        }
    }

    async function fetchMoreActivityLog () {
        if (!hasMore.value || !nextCursor.value || !currentSpaceId.value) return;

        fetchingActivityLog.value = true;

        try {
            const response = await fetchSpaceActivityLogApi(currentSpaceId.value, nextCursor.value);
            activityLogEntries.value = [...activityLogEntries.value, ...response.entries];
            hasMore.value = response.hasMore;
            nextCursor.value = response.nextCursor;
        } catch (error) {
            hasMore.value = false;
            nextCursor.value = undefined;
            throw error;
        } finally {
            fetchingActivityLog.value = false;
        }
    }

    /**
     * Optimistically prepend a local activity log entry so the current user sees
     * their own action immediately, without waiting for a server round-trip or websocket.
     */
    function addLocalLog ({ action, itemType, itemId, itemDisplayName, userId, parentLog }: AddLocalLogPayload) {
        const nowSeconds = Math.floor(Date.now() / 1000);

        const entry: ActivityLogEntry = {
            id: `local-${++localIdCounter}`,
            action,
            itemType,
            itemId,
            itemDisplayName,
            userId,
            createdAt: { _seconds: nowSeconds, _nanoseconds: 0 },
            ...(parentLog ? { parentLog } : {}),
        };

        activityLogEntries.value = [entry, ...activityLogEntries.value];
    }

    return {
        activityLogEntries,
        fetchingActivityLog,
        hasMore,
        fetchActivityLog,
        fetchMoreActivityLog,
        addLocalLog,
    };
}
